import type * as pf from '@amirada/lib-shared'
import { PfCmdType } from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import { useInitStore } from '@base/stores/initStore'
import { useAppIndexStore } from '@base/stores/indexStore'
import { ut_isSpecialPage, ut_navigateNewRoute, ut_returnNewRoute } from '@base/utils/utils'
import { usePfinderAPI } from '@base/composables/pfinderAPI'
import { CalendarDate } from '@internationalized/date'

export function useSettings() {
  const indexStore = useAppIndexStore()
  const initStore = useInitStore()

  // Get all init data
  const initData = computed((): pf.InitData | undefined => {
    return initStore.getInitData(indexStore.state.paramData)
  })

  // InfoData
  const infoData = computed((): pf.InfoData | undefined => {
    return initData.value?.infoData
  })

  // SysInfo
  const sysInfo = computed((): Record<string, string> | undefined => {
    return initData.value?.sysInfo
  })

  // Languages
  const langData = computed((): tp.ISelectItem[] | undefined => {
    if (!initData.value?.langData) return
    const res: tp.ISelectItem[] = []
    for (const item of initData.value.langData) {
      res.push({
        label: item.desc,
        value: item.cod.toLowerCase(),
      })
    }
    return res
  })
  const langValue = computed((): string => {
    return indexStore.state.newParamData.lcda
  })

  // Countries
  const countryData = computed((): tp.ISelectItem[] | undefined => {
    return modifyRestData(initData.value?.restData.rcco, false)
  })
  const countryValue = computed((): string => {
    return indexStore.state.newParamData.rcco
  })

  // rcre1
  const rcre1Data = computed((): tp.ISelectItem[] | undefined => {
    return modifyRestData(initData.value?.restData.rcre1, true)
  })
  const rcre1Value = ref(indexStore.state.paramData.queryUrlServer.rcre1 || '')
  /* watch(() => indexStore.state.paramData.queryUrlServer.rcre1, () => {
    rcre1Value.value = indexStore.state.paramData.queryUrlServer.rcre1 || ''
  }) */

  // rcre2
  const rcre2Data = computed((): tp.ISelectItem[] | undefined => {
    return modifyRestData(initData.value?.restData.rcre2, true)
  })
  const rcre2Value = ref(indexStore.state.paramData.queryUrlServer.rcre2 || '')
  /* watch(() => indexStore.state.paramData.queryUrlServer.rcre2, () => {
    rcre2Value.value = indexStore.state.paramData.queryUrlServer.rcre2 || ''
  }) */

  /*
   * rcst — a MULTIPLE select, unlike rcre1/rcre2.
   *
   * The object status takes a list of allowed statuses since 26.08.2026 ('oac,oad'), so more than
   * one can be picked. No '-' entry either: with a multiple select an empty selection IS the empty
   * state, and a '-' item would be a value competing with it.
   *
   * ⚑ Empty means "send nothing", and the server then applies its own default (currently OAC, i.e.
   * only active objects). Deliberately NOT pre-selected here: which statuses are visible by default
   * is the server's policy, and a client that mirrors it shows the wrong thing the day that policy
   * changes. Empty says "the server decides", which stays true.
   */
  const rcstData = computed((): tp.ISelectItem[] | undefined => {
    return modifyRestData(initData.value?.restData.rcst, false)
  })
  const rcstValue = ref<string[]>(splitRcst(indexStore.state.paramData.queryUrlServer.rcst))
  // (the commented-out watch that stood here assigned a string and would not compile any more —
  // the watchEffect below has done this job for all three since long before)

  watchEffect(() => {
    const { rcre1, rcre2, rcst } = indexStore.state.paramData.queryUrlServer
    rcre1Value.value = rcre1 || ''
    rcre2Value.value = rcre2 || ''
    rcstValue.value = splitRcst(rcst)
  })

  // Staging
  const stageData = computed((): tp.ISelectItem[] | undefined => {
    if (!initData.value?.stagingInfo) return
    const res: tp.ISelectItem[] = []
    for (const item of initData.value.stagingInfo.stages) {
      res.push({
        label: item + (initData.value.stagingInfo.live === item ? ' (Live)' : ''),
        value: item,
      })
    }
    return res
  })
  const stageValue = computed((): string => {
    return indexStore.state.paramData.queryUrlServer.stage || ''
  })

  // Dops data
  const dopsData = computed((): tp.ISelectItem[] | undefined => {
    if (!initData.value?.dopsData) return
    const res: tp.ISelectItem[] = []
    for (const item in initData.value.dopsData) {
      res.push({
        label: item,
        value: initData.value.dopsData[item]?.toString() || '-1',
      })
    }
    return res
  })
  const dops = computed((): string[] => {
    const res: string[] = []
    const dops: number = Number(initData.value?.dops) || 0
    const binAr = dops.toString(2).split('').map(Number) // Gibt [1, 1, 0, 1] zurück
    for (let i = binAr.length; i >= 0; i--) {
      if (binAr[i] === 1) {
        res.push(Math.pow(2, binAr.length - i - 1).toString())
      }
    }
    return res // initData.value?.dops?.toString() || "0"
  })

  const dateValue = computed((): { start: CalendarDate, end: CalendarDate } => {
    let start: CalendarDate | undefined = undefined
    let end: CalendarDate | undefined = undefined
    const queryDate: string = indexStore.state.paramData.queryUrlServer.date || ''
    if (queryDate) {
      const split = queryDate.split('|')
      start = dateStringToCalendarDate(split[0] || '')
      end = dateStringToCalendarDate(split[1] || '')
    }
    return { start: start || dateStringToCalendarDate(), end: end || dateStringToCalendarDate() }
  })

  function dateStringToCalendarDate(val?: string): CalendarDate {
    const date = val ? new Date(val) : new Date()
    return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
  }

  // New URL with rest params
  function changeRest(key: string, value: string): void {
    const noParams: boolean = key === 'stage'
    const queryAddType: tp.IQueryAddType = {
      noPageParams: noParams,
      noGlobalParams: noParams,
      noPsx: noParams,
      noHash: noParams,
      noCustom: noParams,
      noTab: noParams,
    }
    if (value === '-') { value = '' }
    indexStore.setCartIsInitialized(false)
    const route: tp.IParamRouter = ut_returnNewRoute('', { [key]: value }, '', queryAddType)
    navigateTo(route, { replace: false, external: noParams })
  }

  // New URL with rest params
  function changeDops(values: string[]): void {
    let dops: number = 0
    for (const value of values) {
      dops += Number(value)
    }
    const route: tp.IParamRouter = ut_returnNewRoute('', { dops: dops.toString() }, '', {})
    navigateTo(route, { replace: false })
  }

  // New URL for country and language
  async function changeLangCountry(key: string, value: string) {
    indexStore.setCartIsInitialized(false)

    const country: string = key === 'rcco' ? value : indexStore.state.paramData.rcco
    const language: string = key === 'lcda' ? value : indexStore.state.paramData.lcda
    let urlPath: string = ''

    // Redirect
    if (ut_isSpecialPage(indexStore.state.currentNode.nodeName)) {
      urlPath = indexStore.state.currentNode.slugPath
    }
    else {
      // get new slug
      const pfinderAPI = usePfinderAPI()
      const args: pf.PfCmd<PfCmdType.PATH_TRANS>['arg'] = {
        namePath: indexStore.state.paramData.namePath,
        trans: language,
      }
      const data: tp.IFetchData = {
        paramData: indexStore.state.paramData,
      }
      const res = await pfinderAPI.fetchData(PfCmdType.PATH_TRANS, args, data)
      if (res?.result) {
        urlPath = res.result
      }
    }
    const path = '/' + country + '/' + language + '/' + urlPath
    // Redirect
    ut_navigateNewRoute(path, {}, '', true)
  }

  // Change date
  function changeDate(value: any): void {
    const start = new Date(value.start).toISOString().slice(0, 10)
    const end = new Date(value.end).toISOString().slice(0, 10)
    ut_navigateNewRoute('', { date: [start, end].join('|') }, '', true)
  }

  // Rest data to UI format
  /** The comma list the server takes for rcst, as the array a multiple select works with. */
  function splitRcst(value: string | undefined): string[] {
    return (value || '').split(',').map(v => v.trim()).filter(Boolean)
  }

  /**
   * One or more allowed object statuses. Joined back into the comma list the server expects, and
   * routed through changeRest so the URL is built in exactly one place.
   */
  function changeRcst(values: string[]): void {
    changeRest('rcst', values.join(','))
  }

  function modifyRestData(restData: pf.TPfNode[] | undefined, addEmpty: boolean): tp.ISelectItem[] | undefined {
    if (!restData) { return }
    const res: tp.ISelectItem[] = []
    if (addEmpty) {
      res.push({
        label: '-',
        value: '-',
      })
    }
    for (const node of restData) {
      res.push({
        label: node.desc,
        value: node.cod.toLowerCase(),
      })
    }
    return res
  }

  function getSelectItemLabel(selectData: tp.ISelectItem[], value: string): string {
    return selectData.find(s => s.value === value)?.label || ''
  }

  return {
    langData,
    langValue,
    countryData,
    countryValue,
    rcre1Data,
    rcre1Value,
    rcre2Data,
    rcre2Value,
    rcstData,
    rcstValue,
    stageData,
    stageValue,
    dateValue,
    changeLangCountry,
    changeRest,
    changeRcst,
    changeDate,
    infoData,
    sysInfo,
    dops,
    dopsData,
    changeDops,
    getSelectItemLabel,
    indexStore,
  }
}
